import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwButton } from '../../ui/button/uw-button';
import { UwSkeleton } from '../../ui/skeleton/uw-skeleton';
import { UwBadge } from '../../ui/badge/uw-badge';
import { QueueStore } from '../../../core/stores/queue.store';
import { CopilotStore } from '../../../core/stores/copilot.store';
import { WorkbenchStore } from '../../../core/stores/workbench.store';
import { ToastService } from '../../../core/services/toast.service';
import type { AiBriefing, BriefingInsight } from '../../../core/models/ai.model';
import type { RiskTone } from '../../../core/models/types';

interface RenderedInsight {
  text: string;
  strong: boolean;
}

@Component({
  selector: 'uw-ai-briefing',
  standalone: true,
  imports: [UwIcon, UwButton, UwSkeleton, UwBadge, DatePipe],
  template: `
    <div class="flex h-full flex-col gap-4">
      @if (loading()) {
        <uw-skeleton height="18px" width="70%" />
        <uw-skeleton height="14px" width="40%" />
        <div class="space-y-2">
          <uw-skeleton height="60px" />
          <uw-skeleton height="60px" />
          <uw-skeleton height="60px" />
        </div>
      } @else if (briefing(); as b) {
        <div>
          <h2 class="typo-heading text-primary">{{ b.greeting }}</h2>
          <p class="mt-0.5 flex items-center gap-1.5 text-[12px] text-text-muted">
            <uw-icon name="calendar" [size]="13" />
            {{ b.date | date: 'fullDate' }} · {{ b.openSubmissions }} Open Submissions
          </p>
        </div>

        <div class="grid grid-cols-2 gap-2">
          @for (stat of statCards(); track stat.label) {
            <div class="rounded-lg border border-line bg-surface-elevated px-3 py-2">
              <div class="typo-label">{{ stat.label }}</div>
              <div class="mt-0.5 flex items-center gap-1.5 typo-mono text-[17px] font-bold" [class]="stat.toneClass">
                {{ stat.value }}
              </div>
            </div>
          }
        </div>

        <div class="flex items-center gap-1.5">
          <uw-icon name="sparkles" [size]="14" class="text-ai" />
          <span class="typo-label">AI Morning Briefing</span>
          <uw-badge tone="ai">Copilot</uw-badge>
        </div>

        <div class="space-y-3">
          @for (insight of b.insights; track $index) {
            <div class="rounded-lg border border-line bg-surface-elevated/60 p-3.5" [class]="borderFor(insight)">
              <p class="text-[12.5px] leading-relaxed text-secondary">
                @for (part of renderInsight(insight); track $index) {
                  @if (part.strong) {
                    <strong class="font-semibold text-primary">{{ part.text }}</strong>
                  } @else {
                    <span>{{ part.text }}</span>
                  }
                }
              </p>
            </div>
          }
        </div>

        <div class="mt-auto border-t border-line pt-3">
          <p class="typo-label mb-2">Suggested actions</p>
          <div class="flex flex-wrap gap-2">
            <uw-button variant="ai" size="sm" icon="alert-triangle" (click)="focusHigh()">Review {{ b.highPriority }} high priority</uw-button>
            <uw-button variant="secondary" size="sm" icon="file-check" (click)="bulkQuote()">Bulk issue {{ b.straightThrough }} quotes</uw-button>
            <uw-button variant="ghost" size="sm" icon="mail" (click)="reviewChasers()">Review chasers</uw-button>
          </div>
        </div>
      }
    </div>
  `,
})
export class UwAiBriefing {
  private readonly router = inject(Router);
  private readonly queue = inject(QueueStore);
  private readonly copilot = inject(CopilotStore);
  private readonly workbench = inject(WorkbenchStore);
  private readonly toast = inject(ToastService);

  readonly briefing = input<AiBriefing | null>(null);
  readonly loading = input(false);

  protected readonly statCards = computed(() => {
    const b = this.briefing();
    if (!b) return [];
    return [
      { label: 'Open submissions', value: b.openSubmissions, toneClass: 'text-primary' },
      { label: 'High priority', value: b.highPriority, toneClass: 'text-warning' },
      { label: 'Straight-through', value: b.straightThrough, toneClass: 'text-success' },
      { label: 'Blocked', value: b.blocked, toneClass: 'text-danger' },
    ];
  });

  protected borderFor(insight: BriefingInsight): string {
    const tones: Record<RiskTone, string> = {
      success: 'border-l-[3px] border-l-success',
      warning: 'border-l-[3px] border-l-warning',
      danger: 'border-l-[3px] border-l-danger',
      info: 'border-l-[3px] border-l-info',
      neutral: '',
    };
    return tones[insight.tone] ?? '';
  }

  protected renderInsight(insight: BriefingInsight): RenderedInsight[] {
    const b = this.briefing();
    let text = insight.text;
    if (b) {
      text = text
        .replaceAll('{high}', String(b.highPriority))
        .replaceAll('{st}', String(b.straightThrough))
        .replaceAll('{blocked}', String(b.blocked));
    }
    const parts: RenderedInsight[] = [{ text, strong: false }];
    for (const highlight of insight.highlights) {
      const flattened = parts.flatMap((part) => {
        if (part.strong) return [part];
        const idx = part.text.indexOf(highlight);
        if (idx === -1) return [part];
        return [
          { text: part.text.slice(0, idx), strong: false },
          { text: highlight, strong: true },
          { text: part.text.slice(idx + highlight.length), strong: false },
        ];
      });
      parts.length = 0;
      parts.push(...flattened.filter((p) => p.text.length > 0));
    }
    return parts;
  }

  protected focusHigh(): void {
    this.workbench.setMode('copilot');
    this.copilot.setView('triage');
    this.queue.setFilter('high');
    this.queue.setSearch('');
    void this.router.navigate(['/workbench']);
  }

  protected bulkQuote(): void {
    this.copilot.setActionBanner('Issued quotes to brokers');
    this.toast.success('Quotes issued', 'The straight-through renewals were sent to their brokers.');
  }

  protected reviewChasers(): void {
    this.copilot.setActionBanner('Chasers drafted for 2 blocked accounts');
    this.toast.info('Chasers drafted', 'Review the broker chasers in the Action Hub.');
  }
}
